import React, { useState, useEffect } from "react";
import "../AddAdvertisement.css";
import { isSpecialUser } from "../../../../../utils/environment";
import WeekdayOnlyDatePicker from "../components/WeekdayOnlyDatePicker";

const AppReviewIosPage = ({
  formData,
  onInputChange,
  user,
  onNext,
  endSubmitTime,
  step = 1,
  keywordRanks = [],
  addKeywordRank,
  removeKeywordRank,
  updateKeywordRank,
  zoomSettings,
}) => {
  const [currentStep, setCurrentStep] = useState(step);
  const [googlePlayUrlError, setGooglePlayUrlError] = useState("");
  const [appStoreUrlError, setAppStoreUrlError] = useState("");

  // step prop이 변경될 때 currentStep 업데이트
  useEffect(() => {
    setCurrentStep(step);
  }, [step]);

  // adStartDate와 adEndDate가 변경될 때 광고 기간 자동 계산
  useEffect(() => {
    if (formData.adStartDate && formData.adEndDate) {
      const startDate = new Date(formData.adStartDate);
      const endDate = new Date(formData.adEndDate);

      if (endDate >= startDate) {
        const timeDiff = endDate.getTime() - startDate.getTime();
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // 시작일과 종료일 포함

        // console.log("📅 useEffect로 광고 기간 계산:", {
        //   startDate: formData.adStartDate,
        //   endDate: formData.adEndDate,
        //   timeDiff: timeDiff,
        //   daysDiff: daysDiff,
        //   explanation: `${formData.adStartDate}부터 ${formData.adEndDate}까지 ${daysDiff}일 (시작일과 종료일 모두 포함)`,
        // });

        onInputChange("adDuration", daysDiff.toString());
      }
    }
  }, [formData.adStartDate, formData.adEndDate]);

  // 내일 날짜 계산 (한국 시간 기준)
  const getTomorrowDate = () => {
    const now = new Date();
    const koreaTime = new Date(now.getTime() + 9 * 60 * 60 * 1000); // UTC+9 (한국 시간)
    const tomorrow = new Date(koreaTime);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  };

  // 오늘 날짜 계산 (한국 시간 기준)
  const getTodayDate = () => {
    const now = new Date();
    const koreaTime = new Date(now.getTime() + 9 * 60 * 60 * 1000); // UTC+9 (한국 시간)
    return koreaTime.toISOString().split("T")[0];
  };

  // 최소 날짜 (내일)
  const minDate = getTomorrowDate();
  const todayDate = getTodayDate();

  // 특정 유저는 오늘부터, 일반 유저는 내일부터
  const userIsSpecial = isSpecialUser(user?.id);
  const effectiveMinDate = userIsSpecial ? todayDate : minDate;

  // 광고 종료일의 최소 날짜 (광고 시작일이 있으면 그 날짜, 없으면 내일)
  const getEndDateMin = () => {
    if (formData.adStartDate) {
      return formData.adStartDate;
    }
    return effectiveMinDate;
  };

  // 날짜 검증 함수
  const validateDate = (dateString) => {
    if (!dateString) return "날짜를 선택해주세요.";

    const selectedDate = new Date(dateString);
    const tomorrow = new Date(getTomorrowDate());

    // 유효한 날짜인지 확인
    if (isNaN(selectedDate.getTime())) {
      return "올바른 날짜 형식이 아닙니다.";
    }

    // 특정 유저가 아닌 경우에만 내일 이후인지 확인
    if (!userIsSpecial && selectedDate < tomorrow) {
      return "광고 시작일은 내일 이후여야 합니다.";
    }

    return "";
  };

  // 광고 시작일 입력 처리
  const handleAdStartDateChange = (e) => {
    const value = e.target.value;

    // 날짜 검증
    if (value) {
      const error = validateDate(value);
      if (error) {
        // 에러가 있으면 날짜를 초기화
        onInputChange("adStartDate", "");
        alert(error);
        return;
      }
      // 광고 시작일: 주말(토·일) 선택 불가 (VVIP 또는 특정 유저는 주말 가능)
      const selectedDate = new Date(value);
      const dayOfWeek = selectedDate.getDay();
      const allowWeekendByGrade = user?.price_grade?.name === "VVIP";
      const allowWeekend = allowWeekendByGrade || userIsSpecial;

      if ((dayOfWeek === 0 || dayOfWeek === 6) && !allowWeekend) {
        onInputChange("adStartDate", "");
        alert("광고 시작일은 평일만 선택 가능합니다.");
        return;  
      }
    }

    // 광고 시작일 업데이트
    onInputChange("adStartDate", value);

    // 광고 시작일이 변경되면 모든 계산 재실행
    if (value) {
      // 진행 일수가 있으면 종료일을 다시 계산 (우선순위)
      const duration = parseInt(formData.adDuration) || 0;
      if (duration > 0) {
        const startDate = new Date(value);
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + duration - 1); // 시작일 포함이므로 -1
        const endDateString = endDate.toISOString().split("T")[0];
        onInputChange("adEndDate", endDateString);
      } else if (formData.adEndDate) {
        // 종료일이 있으면 진행 일수를 다시 계산
        calculateAdDuration();
      }
    } else {
      // 광고 시작일이 비어있으면 종료일과 진행 일수 초기화
      onInputChange("adEndDate", "");
      onInputChange("adDuration", "");
    }
  };

  // 광고 종료일 입력 처리
  const handleAdEndDateChange = (e) => {
    const value = e.target.value;
    onInputChange("adEndDate", value);

    // 날짜 검증
    if (value) {
      const error = validateDate(value);
      if (error) {
        // 에러가 있으면 날짜를 초기화
        onInputChange("adEndDate", "");
        alert(error);
      }
    }

    // 광고 기간 자동 계산
    calculateAdDuration();
  };

  // 진행 일수 입력 처리
  const handleAdDurationChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, ""); // 숫자만 허용

    if (!formData.adStartDate) {
      alert("광고 시작일을 먼저 선택해주세요.");
      return;
    }

    if (value === "") {
      onInputChange("adDuration", "");
      onInputChange("adEndDate", "");
      return;
    }

    const duration = parseInt(value) || 0;

    if (duration <= 0) {
      alert("진행 일수는 1일 이상이어야 합니다.");
      return;
    }

    // 광고 시작일로부터 진행 일수만큼 더해서 종료일 계산
    const startDate = new Date(formData.adStartDate);
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + duration - 1); // 시작일 포함이므로 -1

    const endDateString = endDate.toISOString().split("T")[0];

    onInputChange("adDuration", duration.toString());
    onInputChange("adEndDate", endDateString);
  };

  // 광고 기간 자동 계산 함수
  const calculateAdDuration = () => {
    if (formData.adStartDate && formData.adEndDate) {
      const startDate = new Date(formData.adStartDate);
      const endDate = new Date(formData.adEndDate);

      if (endDate >= startDate) {
        const timeDiff = endDate.getTime() - startDate.getTime();
        const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // 시작일과 종료일 포함

        // console.log("📅 광고 기간 계산:", {
        //   startDate: formData.adStartDate,
        //   endDate: formData.adEndDate,
        //   timeDiff: timeDiff,
        //   daysDiff: daysDiff,
        //   explanation: `${formData.adStartDate}부터 ${formData.adEndDate}까지 ${daysDiff}일 (시작일과 종료일 모두 포함)`,
        // });

        onInputChange("adDuration", daysDiff.toString());
      }
    }
  };

  // 결제 금액 자동 계산 함수 (1회 단가 소수점 지원)
  const calculatePrice = () => {
    const totalQuantity = parseInt(formData.totalQuantity) || 0;
    const userPriceByOne = parseFloat(formData.userPriceByOne) || 0;

    const totalPrice = totalQuantity * userPriceByOne;

    onInputChange("price", totalPrice.toString());
  };

  // 신청 수량, 단가가 변경될 때마다 결제 금액 계산
  useEffect(() => {
    calculatePrice();
  }, [formData.totalQuantity, formData.userPriceByOne]);

  // URL 포맷팅 함수
  const formatUrl = (url) => {
    if (!url) return "";

    // URL 정리 (앞뒤 공백 제거)
    let formattedUrl = url.trim();

    // @ 기호가 있으면 제거
    if (formattedUrl.startsWith("@")) {
      formattedUrl = formattedUrl.substring(1);
    }

    // http:// 또는 https://가 없으면 https:// 추가
    if (
      !formattedUrl.startsWith("http://") &&
      !formattedUrl.startsWith("https://")
    ) {
      formattedUrl = "https://" + formattedUrl;
    }

    return formattedUrl;
  };

  // Google Play URL 검증 함수
  const validateGooglePlayUrl = (url) => {
    if (!url) return "";

    // 일반적인 URL 패턴 검증
    const urlPattern = /^https?:\/\/[^\s/$.?#].[^\s]*$/;

    if (!urlPattern.test(url)) {
      return "올바른 URL 형식이 아닙니다.";
    }

    return "";
  };

  // App Store URL 검증 함수
  const validateAppStoreUrl = (url) => {
    if (!url) return "";

    // 일반적인 URL 패턴 검증
    const urlPattern = /^https?:\/\/[^\s/$.?#].[^\s]*$/;

    if (!urlPattern.test(url)) {
      return "올바른 URL 형식이 아닙니다.";
    }

    return "";
  };

  // Google Play URL 입력 처리
  const handleGooglePlayUrlChange = (e) => {
    const value = e.target.value;
    onInputChange("googlePlayUrl", value);
    setGooglePlayUrlError(""); // 입력 중에는 에러 메시지 제거
  };

  // Google Play URL 포커스 아웃 처리
  const handleGooglePlayUrlBlur = (e) => {
    const value = e.target.value;
    if (value) {
      const formattedUrl = formatUrl(value);
      const error = validateGooglePlayUrl(formattedUrl);

      if (error) {
        setGooglePlayUrlError(error);
      } else {
        onInputChange("googlePlayUrl", formattedUrl);
        setGooglePlayUrlError("");
      }
    }
  };

  // App Store URL 입력 처리
  const handleAppStoreUrlChange = (e) => {
    const value = e.target.value;
    onInputChange("appStoreUrl", value);
    setAppStoreUrlError(""); // 입력 중에는 에러 메시지 제거
  };

  // App Store URL 포커스 아웃 처리
  const handleAppStoreUrlBlur = (e) => {
    const value = e.target.value;
    if (value) {
      const formattedUrl = formatUrl(value);
      const error = validateAppStoreUrl(formattedUrl);

      if (error) {
        setAppStoreUrlError(error);
      } else {
        onInputChange("appStoreUrl", formattedUrl);
        setAppStoreUrlError("");
      }
    }
  };

  const handleSubmit = () => {
    if (!user?.id) {
      onNext();
      return;
    }
    setCurrentStep(2);
  };

  const handleBackFromConfirmation = () => {
    setCurrentStep(1);
  };

  const handleConfirmOrder = () => {
    setCurrentStep(3);
  };

  // 키워드 입력 변경 핸들러 (입력 중에는 원본 유지, blur/Enter 시 trim + 중복 검사)
  const handleKeywordChange = (e, actualIndex) => {
    const inputValue = e.target.value;
    updateKeywordRank(actualIndex, "keyword", inputValue);
  };

  // 키워드 blur 핸들러 (trim + 중복 검사)
  const handleKeywordBlurWithValidation = (keyword, actualIndex) => {
    const trimmedKeyword = keyword.trim();

    if (keyword !== trimmedKeyword) {
      updateKeywordRank(actualIndex, "keyword", trimmedKeyword);
    }

    if (trimmedKeyword !== "") {
      const duplicateActualIndex = keywordRanks.findIndex(
        (item, idx) =>
          idx !== actualIndex &&
          item.keyword.trim() !== "" &&
          item.keyword.trim().toLowerCase() === trimmedKeyword.toLowerCase()
      );

      if (duplicateActualIndex !== -1) {
        const duplicateKeyword = keywordRanks[duplicateActualIndex];

        if (duplicateKeyword.selected) {
          alert("이미 등록된 키워드입니다.");
          removeKeywordRank(actualIndex);
          return;
        }

        updateKeywordRank(duplicateActualIndex, "selected", true);
        removeKeywordRank(actualIndex);
        return;
      }
    }
  };

  // 키워드 입력 필드 키 다운 처리 (엔터 키)
  const handleKeywordKeyDown = (e, keyword, actualIndex) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const trimmedKeyword = keyword.trim();

      if (keyword !== trimmedKeyword) {
        updateKeywordRank(actualIndex, "keyword", trimmedKeyword);
      }

      if (trimmedKeyword !== "") {
        const duplicateActualIndex = keywordRanks.findIndex(
          (item, idx) =>
            idx !== actualIndex &&
            item.keyword.trim() !== "" &&
            item.keyword.trim().toLowerCase() === trimmedKeyword.toLowerCase()
        );

        if (duplicateActualIndex !== -1) {
          const duplicateKeyword = keywordRanks[duplicateActualIndex];

          if (duplicateKeyword.selected) {
            alert("이미 등록된 키워드입니다.");
            removeKeywordRank(actualIndex);
            return;
          }

          updateKeywordRank(duplicateActualIndex, "selected", true);
          removeKeywordRank(actualIndex);
          return;
        }
      }

      e.target.blur();
    }
  };

  // 주문 확인 화면
  if (currentStep === 2) {
    return (
      <div className="shopping-visit-confirmation">
        {/* 빈 공간 */}
        <div className="spacer"></div>
        {/* 1. 상품 정보 입력 섹션 */}
        <div className="product-info-section">
          <div className="product-image-container">
            <div className="product-image">
              {formData.imageUrl ? (
                <img
                  src={formData.imageUrl}
                  alt="플레이스 이미지"
                  className="product-image-display"
                />
              ) : (
                <div className="image-placeholder">
                  <span>플레이스 이미지</span>
                </div>
              )}
            </div>
          </div>

          <div className="product-info-grid">
            {/* 첫 번째 줄
            <div className="info-row">
              <div className="form-field">
                <label>카테고리 상품 URL</label>
                <input type="text" value="" readOnly />
              </div>
              <div className="form-field">
                <label>카테고리 상품명</label>
                <input type="text" value="" readOnly />
              </div>
            </div> */}

            {/* 두 번째 줄 */}
            <div className="info-row">
              <div className="form-field">
                <label>어플명</label>
                <input
                  type="text"
                  value={formData.missionName || ""}
                  readOnly
                />
              </div>
              <div className="form-field">
                <label>어플 이미지 주소</label>
                <input type="text" value={formData.imageUrl || ""} readOnly />
              </div>
            </div>

            {/* 세 번째 줄 */}
            <div className="info-row">
              {/* <div className="form-field">
                <label>구글 플레이스토어 URL 주소</label>
                <input
                  type="text"
                  value={formData.googlePlayUrl || ""}
                  readOnly
                />
              </div> */}
              <div className="form-field">
                <label>앱스토어 URL 주소</label>
                <input
                  type="text"
                  value={formData.appStoreUrl || ""}
                  readOnly
                />
              </div>
              {/* <div className="form-field">
                <label>상품번호</label>
                <input type="text" value="" readOnly />
              </div> */}
              {/* <div className="form-field">
                <label>상품 이미지 주소</label>
                <input
                  type="text"
                  value={formData.imageUrl || ""}
                  placeholder="https://shopping..."
                  readOnly
                />
              </div> */}
            </div>

            {/* 네 번째 줄 */}
            <div className="info-row">
              <div className="form-field">
                <label>메인키워드</label>
                <input
                  type="text"
                  value={`${formData.mainKeyword?.name || 0}회`}
                  readOnly
                />
              </div>
              <div className="form-field">
                <label>신청 수량(총수량)</label>
                <input
                  type="text"
                  value={`${formData.totalQuantity || 0}회`}
                  readOnly
                />
              </div>
              {/* <div className="form-field"> 
                <label>광고 기간</label>
                <input
                  type="text"
                  value={`${formData.adDuration || 0}일`}
                  readOnly
                />
              </div> */}
              <div className="form-field">
                <label>광고 시작일</label>
                <input
                  type="text"
                  value={formData.adStartDate || ""}
                  readOnly
                />
              </div>
            </div>
          </div>
        </div>

        <div className="keyword-section">
          <div className="keyword-inputs-grid-ad">
            {/* 동적으로 키워드 리스트 표시 */}
            {keywordRanks.map((item, originalIndex) =>
              item.selected ? (
                <div key={originalIndex} className="keyword-input-item">
                  <label>
                    {keywordRanks.filter(
                      (_, i) => i <= originalIndex && keywordRanks[i].selected
                    ).length === 1
                      ? "*"
                      : ""}
                    유입용 키워드
                    {
                      keywordRanks.filter(
                        (_, i) => i <= originalIndex && keywordRanks[i].selected
                      ).length
                    }
                    {keywordRanks.filter(
                      (_, i) => i <= originalIndex && keywordRanks[i].selected
                    ).length === 1
                      ? "(필수)"
                      : "(선택)"}
                  </label>
                  <input
                    type="text"
                    placeholder={`${
                      zoomSettings?.allowRanking || 30
                    }위 내 키워드 입력`}
                    value={item.keyword || ""}
                    onChange={(e) => handleKeywordChange(e, originalIndex)}
                    onBlur={(e) =>
                      handleKeywordBlurWithValidation(
                        e.target.value,
                        originalIndex
                      )
                    }
                    onKeyDown={(e) =>
                      handleKeywordKeyDown(e, e.target.value, originalIndex)
                    }
                    className="keyword-input-field"
                  />
                </div>
              ) : null
            )}

            {/* 추가 버튼 */}
            {keywordRanks.filter((item) => item.selected).length <= 10 && (
              <button className="add-keyword-btn" onClick={addKeywordRank}>
                +
              </button>
            )}
          </div>
        </div>

        {/* 3. 가격 및 수량 정보 섹션 */}
        <div className="pricing-section">
          <div className="pricing-grid">
            <div className="form-field">
              <label>1회 단가</label>
              <input
                type="text"
                value={`${(parseFloat(formData.userPriceByOne) || 0).toLocaleString("ko-KR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}원`}
                readOnly
              />
            </div>

            <div className="form-field">
              <label>신청 수량</label>
              <input
                type="text"
                value={`${formData.totalQuantity}회`}
                readOnly
              />
            </div>

            <div className="form-field">
              <label>결제 금액</label>
              <input
                type="text"
                value={`${(parseFloat(formData.price) || 0).toLocaleString("ko-KR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}원`}
                readOnly
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // 완료 화면
  if (currentStep === 3) {
    return (
      <div className="advertisement-complete">
        <div className="complete-content">
          <div className="complete-icon">✅</div>
          <h2 className="complete-title">광고 등록이 완료되었습니다</h2>
          <p className="complete-description">
            등록하신 광고는 관리자 검토 후 승인됩니다.
            <br />
            승인 상태는 내 광고 확인에서 확인하실 수 있습니다.
          </p>
          <div className="complete-actions">
            <button
              className="complete-btn primary"
              onClick={() => onNext("myAdvertisement")}
            >
              내 광고 확인
            </button>
            <button
              className="complete-btn secondary"
              onClick={() => setCurrentStep(1)}
            >
              새 광고 등록
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-review-page">
      {/* <div className="page-description">
        <h3>앱 리뷰 광고</h3>
        <p>
          앱스토어에서 앱 리뷰를 작성하여 리뷰 수를 높이는 광고 서비스입니다.
        </p>
      </div> */}

      <div className="add-form-fields">
        {/* 첫 번째 줄: 구글 플레이스토어 URL 주소, 앱스토어 URL 주소 */}
        <div className="add-form-row">
          {/* <div className="add-form-field">
            <label>구글 플레이스토어 URL 주소</label>
            <input
              type="text"
              placeholder="https://play.google.com/store/apps/details..."
              value={formData.googlePlayUrl}
              onChange={handleGooglePlayUrlChange}
              onBlur={handleGooglePlayUrlBlur}
              disabled={!user?.id}
              className={googlePlayUrlError ? "error" : ""}
            />
            {googlePlayUrlError && (
              <p className="error-message">{googlePlayUrlError}</p>
            )}
          </div> */}
          <div className="add-form-field">
            <label>앱스토어 URL 주소</label>
            <input
              type="text"
              placeholder="https://apps.apple.com/kr/app/..."
              value={formData.appStoreUrl}
              onChange={handleAppStoreUrlChange}
              onBlur={handleAppStoreUrlBlur}
              disabled={!user?.id}
              className={appStoreUrlError ? "error" : ""}
            />
            {appStoreUrlError && (
              <p className="error-message">{appStoreUrlError}</p>
            )}
          </div>
        </div>

        {/* 두 번째 줄: 메인키워드 */}
        <div className="add-form-row">
          <div className="add-form-field">
            <label>메인키워드</label>
            <input
              type="text"
              placeholder="필수입력"
              value={formData.mainKeyword?.name || ""}
              onChange={(e) => onInputChange("mainKeyword", e.target.value)}
              disabled={!user?.id}
              className="keyword-input"
            />
          </div>
        </div>

        {/* 세 번째 줄: 신청 수량(총 수량) */}
        <div className="add-form-row daily-quantity-row">
          <div className="add-form-field">
            <label>신청 수량(총 수량)</label>
            <input
              type="text"
              placeholder="100의 배수 입력"
              value={formData.totalQuantity}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, "");
                onInputChange("totalQuantity", value);
              }}
              disabled={!user?.id}
            />
          </div>
        </div>

        {/* 네 번째 줄: 광고 시작일, 진행 일수, 광고 종료일 */}
        <div className="add-form-row">
          <div className="add-form-field">
            <label>광고 시작일</label>
            <WeekdayOnlyDatePicker
              value={formData.adStartDate}
              onChange={(dateStr) =>
                handleAdStartDateChange({ target: { value: dateStr } })
              }
              minDate={effectiveMinDate}
              disabled={!user?.id}
              userId={user?.id}
            />
          </div>
          <div className="add-form-field">
            <label>진행 일수</label>
            <input
              type="text"
              placeholder="일수 입력"
              value={formData.adDuration || ""}
              onChange={handleAdDurationChange}
              disabled={!user?.id || !formData.adStartDate}
            />
          </div>
          <div className="add-form-field">
            <label>광고 종료일</label>
            <WeekdayOnlyDatePicker
              value={formData.adEndDate}
              onChange={(dateStr) =>
                handleAdEndDateChange({ target: { value: dateStr } })
              }
              minDate={getEndDateMin()}
              disabled={!user?.id || !formData.adStartDate}
              weekdaysOnly={false}
              userId={user?.id}
            />
          </div>
        </div>

        {/* 다섯 번째 줄: 결제 금액 정보 */}
        <div className="add-form-row">
          <div className="add-form-field">
            <label>1회 단가</label>
            <input
              type="text"
              value={`${(parseFloat(formData.userPriceByOne) || 0).toLocaleString("ko-KR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}원`}
              readOnly
            />
          </div>
          <div className="add-form-field">
            <label>신청 수량</label>
            <input type="text" value={`${formData.totalQuantity}회`} readOnly />
          </div>
          <div className="add-form-field">
            <label>결제 금액</label>
            <input
              type="text"
              value={`${(parseFloat(formData.price) || 0).toLocaleString("ko-KR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}원`}
              readOnly
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppReviewIosPage;
